import cx from 'classnames';
import { GovChip, GovIcon, GovTooltip } from '@gov-design-system-ce/react';
import type { DatoveSchrankyDatovaZpravaDto } from '@gov-nx/api';
import { useLocale } from '@gov-nx/core/service';
import type { DataBoxTimeDivisionType } from '@gov-nx/module/data-box';
import {
	DataBoxFolderType,
	getMessageParams,
	useAutomaticRemovalInfo,
	useDataBoxMessageListContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Chip } from '@gov-nx/ui/web';
import { HighlightTextMemo } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { ActionWrapper } from './ActionWrapper';
import { LeftAction } from './LeftAction';

interface DataBoxMessageItemProps {
	message: DatoveSchrankyDatovaZpravaDto & {
		dnuDoOdstraneni?: number;
		datumSmazaniZISDS?: string;
	};
	timePeriodLabel: DataBoxTimeDivisionType;
}

export const DataBoxMessageItem = ({
	message,
	timePeriodLabel,
}: DataBoxMessageItemProps) => {
	const { t } = useLocale(PageCode['datove-schranky']);
	const {
		messagesFilter: { dataBoxId, folderType, query, messageId },
		messagesSelect: {
			showCheckboxes,
			updateSelectedMessageList,
			selectedMessageList,
		},
		dataBoxDateUtils: { getMessageDate },
	} = useDataBoxMessageListContext();

	const { showRemovalInfo, archivedMessageRemovalInfo, messageRemovalInfo } =
		useAutomaticRemovalInfo(message.datumSmazaniZISDS);

	const {
		isUnread,
		isLongUnread,
		isFolder,
		person,
		highlightWholePerson,
		highlightWholeSubject,
	} = getMessageParams({
		message,
		dataBoxId,
		folderType,
		query,
	});

	const { Received, Trash, Sent, Archived } = DataBoxFolderType;

	// just a type guard
	if (!message.datovaZpravaId) {
		return null;
	}

	return (
		<li
			className={cx(
				'border-b border-(--border-subtlest) [ last-of-type:border-0 ]',
				messageId === message.datovaZpravaId
					? 'bg-(--button-outlined-primary-hover)'
					: 'anim-bg-hover'
			)}>
			<article className="relative flex">
				<ActionWrapper
					showCheckboxes={showCheckboxes}
					link={pageRouteResolver('datove-schranky.schranka.zprava', {
						dataBoxId,
						folderType,
						messageId: message.datovaZpravaId,
					})}>
					<div
						className={cx(
							'flex gap-(--spacing-s-nudge) w-full pl-3 pr-4 py-5 border-l-4',
							messageId === message.datovaZpravaId
								? 'border-border-(--border-subtlest)'
								: 'border-transparent'
						)}>
						<LeftAction
							showCheckboxes={showCheckboxes}
							isUnread={isUnread}
							isArchived={!!message.datumArchivace}
							name={'message' + message.datovaZpravaId}
							isLongUnread={isLongUnread}
							isChecked={selectedMessageList.includes(message.datovaZpravaId)}
							onCheck={() => {
								updateSelectedMessageList(message.datovaZpravaId as number);
							}}
						/>
						<div className="flex flex-col gap-2 w-full">
							<div className="flex justify-between items-center gap-4">
								<h3
									className={cx(
										'text-body-m! text-(--text-primary-color)',
										isUnread && 'font-bold'
									)}>
									{query ? (
										<HighlightTextMemo
											text={person}
											search={highlightWholePerson ? person : query}
										/>
									) : (
										person
									)}
								</h3>
								<time
									className={cx(
										'text-body-xs text-(--text-secondary)',
										isUnread && 'font-bold'
									)}>
									{isFolder(Trash)
										? !!message.datumSmazani &&
											getMessageDate(message.datumSmazani, timePeriodLabel)
										: !!message.datumACasDodani &&
											getMessageDate(message.datumACasDodani, timePeriodLabel)}
								</time>
							</div>
							<div className="flex justify-between items-start gap-2">
								<p
									className={cx(
										'mr-auto text-body-s! text-(--text-secondary)',
										isUnread && 'font-bold'
									)}>
									{query ? (
										<HighlightTextMemo
											text={message.vec as string}
											search={
												highlightWholeSubject ? (message.vec as string) : query
											}
										/>
									) : (
										message.vec
									)}
								</p>

								<div className="flex justify-end items-center gap-2">
									{message.datumArchivace && !isFolder(Archived) && (
										<GovTooltip placement="left">
											<GovTooltip.Trigger>
												<Chip
													color="success"
													size="xs"
													type="outlined">
													<GovIcon
														slot="icon-start"
														name="archived-outline"
														type="templates"
													/>
												</Chip>
											</GovTooltip.Trigger>
											<GovTooltip.Content>
												{t('zpravy.archivovano')}
											</GovTooltip.Content>
										</GovTooltip>
									)}
									{message.vTrezoru && (
										<GovTooltip placement="left">
											<GovTooltip.Trigger>
												<GovChip
													color="warning"
													type="outlined"
													size="xs">
													<GovIcon
														type="templates"
														name="in-trezor"
														slot="icon-start"
													/>
												</GovChip>
											</GovTooltip.Trigger>
											<GovTooltip.Content size="s">
												{t('zpravy.detail.v-datovem-trezoru')}
											</GovTooltip.Content>
										</GovTooltip>
									)}
									{isFolder(Archived, Trash) && (
										<span className="flex gap-2 mt-0.5 text-body-xs text-(--text-secondary)">
											{dataBoxId === message.prijemceId ? (
												<>
													{t('slozky.dorucene')}
													<GovIcon
														name="envelope"
														type="templates"
														size="xs"
														color="default"
													/>
												</>
											) : (
												<>
													{t('slozky.odeslane')}
													<GovIcon
														name="send"
														type="templates"
														size="xs"
														color="default"
													/>
												</>
											)}
										</span>
									)}
									{isUnread && (
										<GovTooltip placement="left">
											<GovTooltip.Trigger>
												<span className="flex grow-0 shrink-0 w-3 h-3 mt-1! rounded-full bg-(--background-status-warning)" />
											</GovTooltip.Trigger>
											<GovTooltip.Content>
												{t('zpravy.neprecteno')}
											</GovTooltip.Content>
										</GovTooltip>
									)}
								</div>
							</div>

							{showRemovalInfo && isFolder(Received, Sent) && (
								<div className="flex gap-2 items-center">
									{message.datumArchivace ? (
										<>
											<GovIcon
												name="info-circle"
												type="components"
												size="xs"
												color="default"
											/>
											<MarkdownRender
												applyClasses={{
													paragraph: 'text-body-xs! text-(--text-secondary)',
												}}>
												{archivedMessageRemovalInfo}
											</MarkdownRender>
										</>
									) : (
										<>
											<GovIcon
												name="trash"
												type="templates"
												size="xs"
												color="default"
											/>
											<MarkdownRender
												applyClasses={{
													paragraph: 'text-body-xs! text-(--text-secondary)',
												}}>
												{messageRemovalInfo}
											</MarkdownRender>
										</>
									)}
								</div>
							)}
							{!!message.dnuDoOdstraneni && isFolder(Trash) && (
								<div className="flex gap-2 items-center">
									<GovIcon
										name="trash"
										type="templates"
										size="xs"
										color="default"
									/>
									<MarkdownRender
										applyClasses={{
											paragraph: 'text-body-xs! text-(--text-secondary)',
										}}>
										{t('zpravy.automaticke-trvale-odstraneni-kopie-za', {
											count: message.dnuDoOdstraneni,
										})}
									</MarkdownRender>
								</div>
							)}
						</div>
					</div>
				</ActionWrapper>
			</article>
		</li>
	);
};
